import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../lib/api';

import type {
  AdminUser,
  ApiKey,
  BillingStateChange,
  CircuitBreaker,
  CircuitBreakerState,
  CronJob,
  DsarRequest,
  ExportJob,
  ImpersonationStartInput,
  MfaResetEvent,
  OffboardingSaga,
  RateLimitConfig,
  RateLimitHit,
  StatusComponent,
  SystemRole,
  TranslationOverride,
  UserSession,
  Workspace,
  WorkspaceMember,
  WorkspaceStatus,
} from './types';

const IAM = '/api/iam';
const SHELL = '/api/shell';

/* --------------------------------- Keys ---------------------------------- */
export const adminKeys = {
  users: ['admin', 'users'] as const,
  user: (id: string) => ['admin', 'users', id] as const,
  userSessions: (id: string) => ['admin', 'users', id, 'sessions'] as const,
  userMfaHistory: (id: string) => ['admin', 'users', id, 'mfa-history'] as const,
  userApiKeys: (id: string) => ['admin', 'users', id, 'api-keys'] as const,
  workspaces: ['admin', 'workspaces'] as const,
  workspace: (id: string) => ['admin', 'workspaces', id] as const,
  workspaceMembers: (id: string) => ['admin', 'workspaces', id, 'members'] as const,
  workspaceBilling: (id: string) => ['admin', 'workspaces', id, 'billing'] as const,
  workspaceDsar: (id: string) => ['admin', 'workspaces', id, 'dsar'] as const,
  systemRoles: ['admin', 'roles', 'system'] as const,
  circuitBreakers: ['admin', 'ops', 'circuit-breakers'] as const,
  rateLimits: ['admin', 'ops', 'rate-limits'] as const,
  rateLimitHits: ['admin', 'ops', 'rate-limit-hits'] as const,
  cronJobs: ['admin', 'ops', 'cron-jobs'] as const,
  exports: ['admin', 'ops', 'exports'] as const,
  translations: (workspaceId?: string) => ['admin', 'ops', 'translations', workspaceId ?? 'global'] as const,
  status: ['admin', 'ops', 'status'] as const,
  serviceHealth: ['admin', 'ops', 'service-health'] as const,
};

/* ---------------------------- Service health ----------------------------- */

export type ServiceCategory =
  | 'ai' | 'email' | 'sms' | 'storage' | 'billing' | 'reputation' | 'monitoring';

export interface ServiceHealthCheck {
  key: string;
  label: string;
  category: ServiceCategory;
  required: boolean;
  configured: boolean;
  /** 'db' = saved via UI; 'env' = read from .env on host; 'none' = nothing set. */
  source?: 'db' | 'env' | 'none';
  reason?: string;
  lastTestStatus?: 'ok' | 'failed' | null;
  lastTestError?: string | null;
}

export interface ServiceHealthResponse {
  services: ServiceHealthCheck[];
  summary: { requiredMissing: number; optionalMissing: number; total: number };
}

export function useServiceHealthQuery() {
  return useQuery({
    queryKey: adminKeys.serviceHealth,
    queryFn: () => apiClient.get<ServiceHealthResponse>(`/api/admin/service-health`),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

/* ----------------------------- Users ------------------------------------- */

function normaliseList<T>(data: unknown): { items: T[]; total: number } {
  if (Array.isArray(data)) return { items: data as T[], total: data.length };
  if (data && typeof data === 'object' && 'items' in data) {
    const obj = data as { items: T[]; total?: number };
    return { items: obj.items ?? [], total: obj.total ?? obj.items?.length ?? 0 };
  }
  return { items: [], total: 0 };
}

export function useAdminUsersQuery(filters: { q?: string; status?: string; workspaceId?: string } = {}) {
  return useQuery({
    queryKey: [...adminKeys.users, filters],
    queryFn: async () =>
      normaliseList<AdminUser>(await apiClient.get<unknown>(`${IAM}/users`, filters)),
    placeholderData: (prev) => prev,
  });
}

export function useAdminUserQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.user(id ?? ''),
    queryFn: () => apiClient.get<AdminUser>(`${IAM}/users/${id}`),
    enabled: Boolean(id),
  });
}

export function useUserSessionsQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.userSessions(id ?? ''),
    queryFn: () => apiClient.get<UserSession[]>(`${IAM}/users/${id}/sessions`),
    enabled: Boolean(id),
  });
}

export function useUserMfaHistoryQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.userMfaHistory(id ?? ''),
    queryFn: () => apiClient.get<MfaResetEvent[]>(`${IAM}/users/${id}/mfa-history`),
    enabled: Boolean(id),
  });
}

export function useUserApiKeysQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.userApiKeys(id ?? ''),
    queryFn: () => apiClient.get<ApiKey[]>(`${IAM}/users/${id}/api-keys`),
    enabled: Boolean(id),
  });
}

export function useCreateAdminUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; fullName?: string; role: string }) =>
      apiClient.post<AdminUser>(`${IAM}/users`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export function useDeleteAdminUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: true }>(`${IAM}/users/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export function useRegenerateTempPasswordMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post<{ tempPassword: string }>(
        `${IAM}/users/${userId}/regenerate-temp-password`,
        {},
      ),
    onSuccess: (_res, userId) => {
      void qc.invalidateQueries({ queryKey: adminKeys.user(userId) });
      void qc.invalidateQueries({ queryKey: adminKeys.userSessions(userId) });
    },
  });
}

export function useStartImpersonationMutation() {
  return useMutation({
    mutationFn: (input: ImpersonationStartInput) =>
      apiClient.post<{ token: string; expiresAt: string }>(`${IAM}/impersonation/start`, input),
  });
}

export function useStartOffboardingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; reason: string; immediate?: boolean }) =>
      apiClient.post<OffboardingSaga>(`${IAM}/users/${input.userId}/offboard`, {
        reason: input.reason,
        immediate: input.immediate ?? false,
      }),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.user(vars.userId) });
    },
  });
}

export function useConfirmOffboardingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sagaId: string) =>
      apiClient.post<OffboardingSaga>(`${IAM}/offboarding/${sagaId}/confirm`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export function useCancelOffboardingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sagaId: string) =>
      apiClient.post<OffboardingSaga>(`${IAM}/offboarding/${sagaId}/cancel`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

/* --------------------------- Workspaces ---------------------------------- */

export function useWorkspacesQuery(filters: { q?: string; status?: WorkspaceStatus } = {}) {
  return useQuery({
    queryKey: [...adminKeys.workspaces, filters],
    queryFn: async () =>
      normaliseList<Workspace>(await apiClient.get<unknown>(`${IAM}/workspaces`, filters)),
    placeholderData: (prev) => prev,
  });
}

export function useWorkspaceQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.workspace(id ?? ''),
    queryFn: () => apiClient.get<Workspace>(`${IAM}/workspaces/${id}`),
    enabled: Boolean(id),
  });
}

// (The dead useCreateWorkspaceMutation / useDeleteWorkspaceMutation stubs lived here with zero
// callers and a stale payload shape — the live hooks are routes/iam/queries.ts
// useCreateWorkspace / useDeleteWorkspace.)

export function useWorkspaceMembersQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.workspaceMembers(id ?? ''),
    queryFn: () => apiClient.get<WorkspaceMember[]>(`${IAM}/workspaces/${id}/members`),
    enabled: Boolean(id),
  });
}

export function useWorkspaceBillingHistoryQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.workspaceBilling(id ?? ''),
    queryFn: () => apiClient.get<BillingStateChange[]>(`${IAM}/workspaces/${id}/billing-history`),
    enabled: Boolean(id),
  });
}

export function useWorkspaceDsarQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.workspaceDsar(id ?? ''),
    queryFn: () => apiClient.get<DsarRequest[]>(`${IAM}/workspaces/${id}/dsar`),
    enabled: Boolean(id),
  });
}

/* ------------------------------ Roles ------------------------------------ */

export function useSystemRolesQuery() {
  return useQuery({
    queryKey: adminKeys.systemRoles,
    // System roles + their (read-only) base grants from the canonical constant.
    queryFn: () => apiClient.get<SystemRole[]>(`${IAM}/roles/system`),
  });
}

/* ----------------------------- Operations -------------------------------- */

export function useCircuitBreakersQuery() {
  return useQuery({
    queryKey: adminKeys.circuitBreakers,
    queryFn: () => apiClient.get<CircuitBreaker[]>(`${SHELL}/ops/circuit-breakers`),
    refetchInterval: 15_000,
  });
}

export function useForceCircuitBreakerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: CircuitBreakerState }) =>
      apiClient.post<CircuitBreaker>(`${SHELL}/ops/circuit-breakers/${id}/force`, { state }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.circuitBreakers });
    },
  });
}

export function useRateLimitsQuery() {
  return useQuery({
    queryKey: adminKeys.rateLimits,
    queryFn: () => apiClient.get<RateLimitConfig[]>(`${SHELL}/ops/rate-limits`),
  });
}

export function useRateLimitHitsQuery() {
  return useQuery({
    queryKey: adminKeys.rateLimitHits,
    queryFn: () => apiClient.get<RateLimitHit[]>(`${SHELL}/ops/rate-limits/hits`),
    refetchInterval: 30_000,
  });
}

export function useUpdateRateLimitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<RateLimitConfig> }) =>
      apiClient.patch<RateLimitConfig>(`${SHELL}/ops/rate-limits/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.rateLimits });
    },
  });
}

export function useCronJobsQuery() {
  return useQuery({
    queryKey: adminKeys.cronJobs,
    queryFn: () => apiClient.get<CronJob[]>(`${SHELL}/ops/cron-jobs`),
    refetchInterval: 60_000,
  });
}

export function useTriggerCronJobMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post<{ ok: true }>(`${SHELL}/ops/cron-jobs/${id}/trigger`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.cronJobs });
    },
  });
}

export function useToggleCronJobMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiClient.patch<CronJob>(`${SHELL}/ops/cron-jobs/${id}`, { enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.cronJobs });
    },
  });
}

export function useTranslationsQuery(workspaceId?: string) {
  return useQuery({
    queryKey: adminKeys.translations(workspaceId),
    queryFn: () =>
      apiClient.get<TranslationOverride[]>(`${SHELL}/ops/translations`, workspaceId ? { workspaceId } : undefined),
  });
}

export function useUpsertTranslationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId?: string | null; locale: string; key: string; value: string }) =>
      apiClient.post<TranslationOverride>(`${SHELL}/ops/translations`, input),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.translations(vars.workspaceId ?? undefined) });
    },
  });
}

export function useDeleteTranslationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: true }>(`${SHELL}/ops/translations/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'ops', 'translations'] });
    },
  });
}

export function useStatusComponentsQuery() {
  return useQuery({
    queryKey: adminKeys.status,
    queryFn: () => apiClient.get<StatusComponent[]>(`${SHELL}/ops/status`),
    refetchInterval: 30_000,
  });
}

export function useUpdateStatusComponentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<StatusComponent> }) =>
      apiClient.patch<StatusComponent>(`${SHELL}/ops/status/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.status });
    },
  });
}
